import { motion } from "framer-motion";
import { zeroOne, zeroTwo, zeroThree } from "../../../assets";

const steps = [
  {
    number: "01",
    title: "Apply",
    subtitle: "Start your journey",
    description:
      "Tell us about yourself, your interests, and where you want your technology career to take you.",
    icon: zeroOne,
  },
  {
    number: "02",
    title: "Interview",
    subtitle: "Show us your potential",
    description:
      "Our team gets to know you, understands your goals, and determines the learning path that fits you best.",
    icon: zeroTwo,
  },
  {
    number: "03",
    title: "Learn & Launch",
    subtitle: "Build. Master. Become.",
    description:
      "Learn through hands-on projects, expert mentorship and real-world challenges — then take your skills into the world.",
    icon: zeroThree,
  },
];

const HowItWorks = () => {
  return (
    <section className="relative overflow-hidden py-28 md:py-36">

      {/* =====================================================
          BACKGROUND
      ===================================================== */}

      <div className="absolute inset-0 bg-[#07171c]" />

      {/* Teal glow */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#59b5cd]/20 blur-[140px]" />

      {/* Gold glow */}
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#ecb32c]/15 blur-[140px]" />

      {/* Grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">

        {/* =====================================================
            HEADER
        ===================================================== */}

        <motion.div
          className="text-center max-w-3xl mx-auto mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >

          {/* Eyebrow */}

          <div className="inline-flex items-center gap-3 mb-6">

            <span className="w-8 h-[1px] bg-[#ecb32c]" />

            <span className="text-[#ecb32c] text-xs md:text-sm font-bold tracking-[0.3em] uppercase">
              Your Journey Starts Here
            </span>

            <span className="w-8 h-[1px] bg-[#ecb32c]" />

          </div>

          <h2 className="text-4xl md:text-6xl font-black text-white leading-[1.05]">
            From curious
            <span className="text-[#59b5cd]"> to capable.</span>
            <br />

            <span className="text-white">
              From learner to
            </span>{" "}

            <span className="bg-gradient-to-r from-[#ecb32c] to-[#f5d477] bg-clip-text text-transparent">
              tech professional.
            </span>
          </h2>

          <p className="mt-7 text-white/60 text-base md:text-lg leading-relaxed">
            Getting started at Zulfah is simple. We take you through a
            carefully designed journey that turns potential into practical,
            career-ready technology skills.
          </p>

        </motion.div>


        {/* =====================================================
            JOURNEY
        ===================================================== */}

        <div className="relative">

          {/* Desktop connecting line */}

          <div className="hidden md:block absolute top-[62px] left-[16%] right-[16%] h-[2px] bg-white/10">

            <motion.div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#59b5cd] via-[#ecb32c] to-[#59b5cd]"
              initial={{ width: "0%" }}
              whileInView={{ width: "100%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
            />

          </div>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">

            {steps.map((step, index) => (

              <motion.div
                key={step.number}
                className="relative group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.2,
                }}
              >

                {/* =================================================
                    NUMBER NODE
                ================================================= */}

                <div className="relative flex justify-center mb-10">

                  {/* Outer glow */}

                  <div className="absolute w-[125px] h-[125px] rounded-full bg-[#59b5cd]/10 blur-2xl group-hover:bg-[#ecb32c]/20 transition-all duration-500" />

                  {/* Node */}

                  <div className="relative w-[125px] h-[125px] rounded-full border border-white/10 bg-[#0c2329]/90 backdrop-blur-xl flex items-center justify-center shadow-2xl group-hover:border-[#ecb32c]/60 transition-all duration-500">

                    {/* Inner ring */}

                    <div className="absolute inset-3 rounded-full border border-[#59b5cd]/20 group-hover:border-[#ecb32c]/40 transition" />

                    {/* Icon */}

                    <div className="relative flex flex-col items-center">

                      <img
                        src={step.icon}
                        alt={step.title}
                        className="w-9 h-9 object-contain mb-2 opacity-90 group-hover:scale-110 transition-transform duration-500"
                      />

                      <span className="text-[#ecb32c] text-xs font-black tracking-widest">
                        {step.number}
                      </span>

                    </div>

                  </div>

                </div>


                {/* =================================================
                    CARD
                ================================================= */}

                <div
                  className="
                    relative
                    h-full
                    rounded-3xl
                    border border-white/10
                    bg-white/[0.045]
                    backdrop-blur-xl
                    p-7 md:p-8
                    overflow-hidden
                    transition-all duration-500
                    group-hover:-translate-y-2
                    group-hover:border-[#59b5cd]/40
                    group-hover:bg-white/[0.07]
                  "
                >

                  {/* Hover gradient */}

                  <div className="absolute inset-0 bg-gradient-to-br from-[#59b5cd]/10 via-transparent to-[#ecb32c]/10 opacity-0 group-hover:opacity-100 transition duration-500" />

                  <div className="relative z-10">

                    {/* Small label */}

                    <p className="text-[#59b5cd] text-xs font-bold uppercase tracking-[0.2em] mb-3">
                      Step {step.number}
                    </p>

                    {/* Title */}

                    <h3 className="text-2xl md:text-3xl font-black text-white">
                      {step.title}
                    </h3>

                    {/* Subtitle */}

                    <p className="mt-2 text-[#ecb32c] font-semibold">
                      {step.subtitle}
                    </p>

                    {/* Description */}

                    <p className="mt-5 text-white/55 leading-relaxed text-sm md:text-base">
                      {step.description}
                    </p>

                    {/* Bottom accent */}

                    <div className="mt-7 flex items-center gap-3">

                      <div className="h-[2px] w-10 bg-[#ecb32c] group-hover:w-16 transition-all duration-500" />

                      <span className="text-white/30 text-xs">
                        ZULFAH ACADEMY
                      </span>

                    </div>

                  </div>

                </div>

              </motion.div>

            ))}

          </div>

        </div>


        {/* =====================================================
            BOTTOM CTA
        ===================================================== */}

        <motion.div
          className="mt-20 relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-r from-[#0c252c] to-[#101b20] p-8 md:p-10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >

          {/* Glow */}

          <div className="absolute right-0 top-0 w-72 h-72 bg-[#ecb32c]/10 blur-[100px]" />

          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">

            <div>

              <div className="flex items-center gap-2 mb-3">

                <span className="w-2 h-2 rounded-full bg-[#59b5cd] animate-pulse" />

                <span className="text-[#59b5cd] text-xs font-bold uppercase tracking-[0.2em]">
                  Ready when you are
                </span>

              </div>

              <h3 className="text-2xl md:text-3xl font-black text-white">
                Your future in tech doesn't
                <span className="text-[#ecb32c]"> start tomorrow.</span>
              </h3>

              <p className="text-white/50 mt-2">
                Take the first step today.
              </p>

            </div>


            <a
              href="/get-started"
              className="
                group
                inline-flex
                items-center
                justify-center
                gap-3
                px-7
                py-4
                rounded-xl
                bg-[#ecb32c]
                text-[#07171c]
                font-bold
                shadow-lg
                shadow-[#ecb32c]/20
                hover:bg-[#f2c44f]
                hover:scale-[1.03]
                transition-all
                whitespace-nowrap
              "
            >

              Start Your Journey

              <span className="text-xl transition-transform group-hover:translate-x-1">
                →
              </span>

            </a>

          </div>

        </motion.div>

      </div>

    </section>
  );
};

export default HowItWorks;